
import { Suspense, lazy, useState, useEffect } from 'react';
import { Cpu } from 'lucide-react';

// Lazy load tab components for better performance
const IntegrationsTab = lazy(() => import('./IntegrationsTab'));
const AlertsTab = lazy(() => import('./AlertsTab'));
const GraphPresetsTab = lazy(() => import('./GraphPresetsTab'));
const StandardsTab = lazy(() => import('./StandardsTab'));
const BPATemplatesTab = lazy(() => import('./BPATemplatesTab'));
const LicensesTab = lazy(() => import('./LicensesTab'));
const PlaceholderTab = lazy(() => import('./PlaceholderTab'));

interface TabContentProps {
  tabName: string;
}

// Loading fallback component
const TabLoading = () => (
  <div className="flex flex-col items-center justify-center py-12">
    <Cpu className="w-12 h-12 text-orange-500 animate-pulse mb-4" />
    <p className="text-muted-foreground">Loading content...</p>
  </div>
);

const TabContent = ({ tabName }: TabContentProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Reset error state when tab changes
  useEffect(() => {
    setHasError(false);
    console.log(`Loading tab: ${tabName}`);
  }, [tabName]);

  // Check if imports exist - adds additional logging for debugging
  const importExists = (name: string) => {
    console.log(`Checking if tab exists: ${name}`);
    return true; // Always return true for now, we'll fall back to placeholder if needed
  };

  // Handle errors during component loading
  const handleError = (error: Error) => {
    console.error(`Error rendering tab ${tabName}:`, error);
    setHasError(true);
    return <PlaceholderTab tabName={tabName} />; 
  };

  // Fallback component if module can't be loaded
  const FallbackTab = () => {
    useEffect(() => {
      console.log(`Using fallback for tab: ${tabName}`);
    }, []);
    return <PlaceholderTab tabName={tabName} />;
  };

  // Render tab content with suspense for better loading experience
  return (
    <div className="py-10 px-8 animate-fade-in">
      <Suspense fallback={<TabLoading />}>
        {(() => {
          try {
            switch (tabName) {
              case "integrations":
                return importExists("IntegrationsTab") ? <IntegrationsTab /> : <FallbackTab />;
              case "alerts":
                return importExists("AlertsTab") ? <AlertsTab /> : <FallbackTab />;
              case "presets":
                return importExists("GraphPresetsTab") ? <GraphPresetsTab /> : <FallbackTab />;
              case "standards":
                return importExists("StandardsTab") ? <StandardsTab /> : <FallbackTab />;
              case "bpa":
                return importExists("BPATemplatesTab") ? <BPATemplatesTab /> : <FallbackTab />;
              case "licenses":
                return importExists("LicensesTab") ? <LicensesTab /> : <FallbackTab />;
              default:
                console.log(`No matching tab for: ${tabName}, using placeholder`);
                return <FallbackTab />;
            }
          } catch (error) {
            return handleError(error as Error);
          }
        })()}
      </Suspense>
    </div>
  );
};

export default TabContent;
